import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CategoryModel } from '../../../models/category.model';
import { UserProfileService } from '../../../../pages/application/management/user-profile/user-profile.service';
import { AuthService } from '../../../services/auth.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { PurchaseActorService } from '../../../services/purchase-actor.service';
import { ENUM_PERMISSIONS } from '../../../enums/permission.enum';
import { FormModalConfirmBaseComponent } from '../form-modal-confirme-base/form-modal-confirm-base.component';
import { PurchaseActorApplyChoice, PurchaseActorApplyModalComponent } from './purchase-actor-apply-modal.component';
import {
  PURCHASE_ACTOR_ROLE_DISPLAY_ORDER,
  PURCHASE_ACTOR_MULTI_ROLES,
  PURCHASE_ACTOR_ROLE_FILTER_FLAG,
  PURCHASE_ACTOR_ROLE_LABELS,
  PurchaseActorAssignPayload,
  PurchaseActorContact,
  PurchaseActorEstablishmentRef,
  PurchaseActorExplicit,
  PurchaseActorRoleEnum,
  PurchaseActorScopeEnum,
  PurchaseActorSourceEnum,
  PurchaseActorUnassignPayload,
  ResolvedPurchaseActors,
} from '../../../models/purchase-actor.model';

/** Établissement minimal requis en scope ESTABLISHMENT — miroir de `PurchaseActorEstablishmentRef`. */
export interface PurchaseActorPanelEstablishment {
  _id: string;
  label: string;
  technicalIdentifier?: string;
}

/** Disposition des rôles : `grid` = colonnes côte à côte (card « Acteurs par défaut »),
 * `stacked` = rôles empilés sous le titre de la famille (panneau de droite). */
export type PurchaseActorNodeEditorLayout = 'grid' | 'stacked';

/** Intitulé du nœud racine (config sans famille) — repris de la card de l'ancien écran. */
export const PURCHASE_ACTOR_DEFAULT_NODE_LABEL = 'Acteurs par défaut';

/**
 * Édition des acteurs achats d'UN nœud (PRED-3139) : « Acteurs par défaut » (`category` = null)
 * ou une famille. Modèle « tout éditable » : chaque rôle affiche son champ, pré-rempli des
 * valeurs RÉSOLUES (héritées comprises) ; la surcharge est un DIFF calculé à l'enregistrement
 * (valeur ≠ héritage => envoyée ; identique/vide => omise => hérite).
 * Badges de provenance vivants (« Sera surchargé », « Reviendra à l'héritage »).
 * Enregistrement explicite (pas d'auto-save) ; propagation aux sous-familles choisie en modale.
 * Utilisé deux fois par `purchase-actors-panel` (card par défaut + famille sélectionnée).
 */
@Component({
  selector: 'purchase-actor-node-editor',
  templateUrl: './purchase-actor-node-editor.component.html',
  styleUrls: ['./purchase-actor-node-editor.component.scss'],
})
export class PurchaseActorNodeEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() scope: PurchaseActorScopeEnum = PurchaseActorScopeEnum.TENANT;
  @Input() establishment?: PurchaseActorPanelEstablishment;
  /** Permissions requises (OU) pour surcharger/éditer/retirer une configuration. Absent = pas de garde. */
  @Input() modifyPermissions?: ENUM_PERMISSIONS[];
  /** Famille éditée ; null = nœud « Acteurs par défaut ». */
  @Input() category: CategoryModel | null = null;
  /** Sous-familles paramétrables du nœud — > 0 : modale de propagation à l'enregistrement. */
  @Input() childrenCount = 0;
  @Input() layout: PurchaseActorNodeEditorLayout = 'stacked';
  /** Émis après un enregistrement / retrait réussi (le parent rafraîchit l'arbre et les autres nœuds). */
  @Output() saved = new EventEmitter<void>();

  readonly PurchaseActorRoleEnum = PurchaseActorRoleEnum;
  readonly allRoles = PURCHASE_ACTOR_ROLE_DISPLAY_ORDER;
  readonly roleLabels = PURCHASE_ACTOR_ROLE_LABELS;

  private subscription = new Subscription();

  // --- Résolution + formulaire d'édition ---
  loadingResolve = false;
  resolved: ResolvedPurchaseActors | null = null;
  /** Résolution SANS la config propre du nœud (héritage « nu ») — base du diff et des resets. */
  inherited: ResolvedPurchaseActors | null = null;
  explicit: PurchaseActorExplicit | null = null;
  /** Rôles portés par le doc explicite au moment du seed (surcharge déjà enregistrée). */
  private roleWasExplicit = new Set<PurchaseActorRoleEnum>();
  /** Valeurs héritées (résolution SANS surcharge propre, fournie par le back) — base du diff de TOUS les rôles. */
  private roleBaseline = new Map<PurchaseActorRoleEnum, PurchaseActorContact[]>();
  /** Incrémenté pour forcer la RECRÉATION du champ d'un rôle (ngFor-epoch dans le template) :
   * le composant partagé ne sait pas être désélectionné de l'extérieur. */
  roleFieldEpoch: Partial<Record<PurchaseActorRoleEnum, number>> = {};
  /** Instantané du seed (ids triés par rôle + valeur du flag) — base du calcul de `dirty`
   * PAR VALEURS, immunisé contre les émissions programmatiques (ex. `control.enable()` émis par
   * base-form-input à la recréation des champs, qui marquait le formulaire sale à tort). */
  private seedRoleIds = new Map<PurchaseActorRoleEnum, string[]>();
  private seedFlagValue = false;
  form: UntypedFormGroup;
  clearChildrenOverrides = false;
  dirty = false;
  saving = false;
  canModify = true;

  roleItems$: Partial<Record<PurchaseActorRoleEnum, Observable<PurchaseActorContact[]>>> = {};
  /** Sélections à restituer dans les champs multi (form-search-multiple ne relit pas le contrôle). */
  rolePreselected: Partial<Record<PurchaseActorRoleEnum, PurchaseActorContact[]>> = {};

  /** Jeton de requête — protège `loadResolve()` contre les réponses tardives d'un appel obsolète
   * (changement rapide de sélection). */
  private resolveSeq = 0;

  constructor(
    private readonly userProfileService: UserProfileService,
    private readonly purchaseActorService: PurchaseActorService,
    private readonly authService: AuthService,
    private readonly snackbarService: SnackbarService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.subscription.add(
      this.authService.getCurrentUserPermissions$().subscribe(permissions => {
        this.canModify = !this.modifyPermissions?.length || this.modifyPermissions.some(p => permissions?.includes(p));
        this.enforceAutorizationOrderCoherence();
      }),
    );
    this.loadResolve();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const nodeChanged = changes.category || changes.scope || changes.establishment;
    if (nodeChanged && !nodeChanged.firstChange) {
      this.resolved = null;
      this.inherited = null;
      this.explicit = null;
      this.roleWasExplicit.clear();
      this.roleBaseline.clear();
      this.seedRoleIds.clear();
      this.dirty = false;
      this.loadResolve();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** Recharge la résolution (ex. l'héritage a changé suite à l'enregistrement d'un autre nœud). */
  reload(): void {
    this.loadResolve();
  }

  private get isDefaultNode(): boolean {
    return !this.category;
  }

  get currentNodeLabel(): string {
    return this.isDefaultNode ? PURCHASE_ACTOR_DEFAULT_NODE_LABEL : this.category.label;
  }

  get breadcrumb(): string {
    const parents = this.category?.parents ?? [];
    return [...parents]
      .sort((a, b) => a.level - b.level)
      .map(parent => parent.label)
      .join(' › ');
  }

  /**
   * Garde de saisie non enregistrée : si le formulaire est sale, confirme avant d'abandonner les
   * modifications en cours (mêmes composant/appel que `ticket-details-basket-type.component.ts`).
   * Utilisée aussi par le parent avant de changer de famille dans l'arbre.
   */
  confirmDiscardIfDirty(proceed: () => void): void {
    if (!this.dirty) {
      proceed();
      return;
    }
    this.dialog
      .open(FormModalConfirmBaseComponent, {
        data: {
          title: 'Modifications non enregistrées',
          message: `Les modifications non enregistrées sur « ${this.currentNodeLabel} » seront perdues. Les abandonner ?`,
        },
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          proceed();
        }
      });
  }

  // --- Résolution ---

  /** Jeton de requête (`resolveSeq`) : une réponse d'un appel obsolète (sélection changée entre
   * temps) est ignorée — évite qu'une résolution lente pour la famille A n'écrase le panneau
   * après que B a été sélectionnée. */
  private loadResolve(): void {
    this.loadingResolve = true;
    const req = ++this.resolveSeq;
    const establishmentId = this.scope === PurchaseActorScopeEnum.ESTABLISHMENT ? this.establishment?._id : undefined;
    this.purchaseActorService.resolve(this.category?._id, establishmentId).subscribe({
      next: response => {
        if (req !== this.resolveSeq) {
          return;
        }
        this.resolved = response.data.resolved;
        // Repli défensif : sans `inherited` (réponse d'une version back antérieure), la
        // résolution complète sert de base — les rôles surchargés perdent alors seulement
        // l'aperçu du « Revenir à l'héritage », pas la fonction.
        this.inherited = response.data.inherited ?? response.data.resolved;
        this.explicit = response.data.explicit;
        this.seedForm();
        this.loadingResolve = false;
      },
      error: () => {
        if (req !== this.resolveSeq) {
          return;
        }
        this.resolved = null;
        this.inherited = null;
        this.explicit = null;
        this.loadingResolve = false;
        this.snackbarService.danger('Erreur lors du chargement de la configuration.');
      },
    });
  }

  // --- Formulaire ---

  private buildForm(): void {
    const controls: Record<string, UntypedFormControl> = {
      autorizationOrder: new UntypedFormControl(false),
    };
    for (const role of this.allRoles) {
      controls[role] = new UntypedFormControl(this.emptyValueFor(role));
    }
    this.form = new UntypedFormGroup(controls);
    // Dirty par COMPARAISON DE VALEURS (état courant vs instantané du seed) : robuste face aux
    // émissions programmatiques des composants de champ (enable()/updateFormControl internes).
    this.subscription.add(this.form.valueChanges.subscribe(() => this.recomputeDirty()));
  }

  private emptyValueFor(role: PurchaseActorRoleEnum): PurchaseActorContact[] | null {
    return role === PurchaseActorRoleEnum.APPROVER ? null : [];
  }

  private getExplicitRoleValue(role: PurchaseActorRoleEnum): PurchaseActorContact[] | PurchaseActorContact | undefined {
    return this.explicit ? this.explicit[role] : undefined;
  }

  private seedForm(): void {
    this.roleWasExplicit.clear();
    this.roleBaseline.clear();
    this.rolePreselected = {};
    for (const role of this.allRoles) {
      const explicitValue = this.getExplicitRoleValue(role);
      const wasExplicit = explicitValue !== undefined;
      if (wasExplicit) {
        this.roleWasExplicit.add(role);
      }
      // Base du diff pour TOUS les rôles : l'héritage « nu » (résolution sans la config propre
      // du nœud, fournie par le back). Une valeur identique au save = rôle omis = hérite —
      // y compris pour une surcharge existante ré-alignée sur l'héritage (elle disparaît).
      this.roleBaseline.set(
        role,
        (this.inherited?.[role]?.users ?? []).map(contact => this.withFullName(contact)),
      );
      // Option A : champ toujours éditable, pré-rempli explicite OU hérité (fullName recalculé,
      // absent des snapshots persistés).
      const rawSeed = wasExplicit ? explicitValue : (this.roleBaseline.get(role) ?? this.emptyValueFor(role));
      const seedValue = this.isMultiRole(role)
        ? ((rawSeed ?? []) as PurchaseActorContact[]).map(contact => this.withFullName(contact))
        : this.withFullName(
            // Baseline héritée = toujours un TABLEAU (users[]) ; l'approbateur (mono) en prend
            // le premier élément — lui passer le tableau produisait un objet corrompu sans
            // fullName => champ affiché vide pour les valeurs héritées.
            wasExplicit
              ? ((rawSeed as PurchaseActorContact) ?? null)
              : (((rawSeed as PurchaseActorContact[])?.[0] as PurchaseActorContact) ?? null),
          );
      if (this.isMultiRole(role)) {
        // Restitution des chips : form-search-multiple ne restaure la sélection que via
        // son @Input preSelectedElement (il ne relit pas le contrôle).
        this.rolePreselected[role] = [...((seedValue ?? []) as PurchaseActorContact[])];
      }
      this.ensureRoleItems(role);
      // Recréation du champ : purge l'état de sélection interne du composant partagé
      // (résiduel de la famille précédente ou d'un reset).
      this.roleFieldEpoch[role] = (this.roleFieldEpoch[role] ?? 0) + 1;
      this.form.get(role)?.setValue(seedValue, { emitEvent: false });
    }
    // Flag « Autorisation sans commande » : valeur explicite du nœud si définie, sinon valeur
    // résolue (signal cascade) — puis mise en cohérence avec les états forcés (approbateur
    // propre => ON, aucun approbateur effectif => OFF), cf. invariant PRED-3207 aménagé.
    const explicitAuthorizationOrder = this.explicit?.autorizationOrder;
    this.form
      .get('autorizationOrder')
      ?.setValue(explicitAuthorizationOrder ?? this.resolved?.autorizationOrder?.value ?? false, { emitEvent: false });
    this.enforceAutorizationOrderCoherence();
    // Instantané du seed pour le calcul du dirty par valeurs.
    for (const role of this.allRoles) {
      this.seedRoleIds.set(role, this.currentRoleIds(role));
    }
    this.seedFlagValue = !!this.form.get('autorizationOrder')?.value;
    this.clearChildrenOverrides = false;
    this.dirty = false;
  }

  /** Dirty = l'état courant (ids par rôle + flag) diffère de l'instantané du seed. */
  private recomputeDirty(): void {
    // La cohérence du flag dépend du champ approbateur : ré-appliquée à CHAQUE changement du
    // formulaire (états forcés vivants pendant la saisie, avant tout enregistrement).
    this.enforceAutorizationOrderCoherence();
    const rolesDirty = this.allRoles.some(role => {
      const seed = this.seedRoleIds.get(role) ?? [];
      const current = this.currentRoleIds(role);
      return current.length !== seed.length || current.some((id, index) => id !== seed[index]);
    });
    const flagDirty = !!this.form.get('autorizationOrder')?.value !== this.seedFlagValue;
    this.dirty = rolesDirty || flagDirty;
  }

  isMultiRole(role: PurchaseActorRoleEnum): boolean {
    return role !== PurchaseActorRoleEnum.APPROVER;
  }

  // --- Option A : rôles toujours éditables, surcharge = diff avec l'héritage ---

  private roleWasExplicitAtSeed(role: PurchaseActorRoleEnum): boolean {
    return this.roleWasExplicit.has(role);
  }

  private currentRoleIds(role: PurchaseActorRoleEnum): string[] {
    const value = this.form.get(role)?.value;
    const contacts: PurchaseActorContact[] = this.isMultiRole(role)
      ? Array.isArray(value)
        ? value
        : []
      : value?._id
        ? [value]
        : [];
    return contacts
      .filter(contact => contact?._id)
      .map(contact => contact._id.toString())
      .sort();
  }

  private baselineRoleIds(role: PurchaseActorRoleEnum): string[] {
    return (this.roleBaseline.get(role) ?? [])
      .filter(contact => contact?._id)
      .map(contact => contact._id.toString())
      .sort();
  }

  /** Le champ dévie-t-il de l'héritage ? (rôles NON explicites uniquement — comparaison d'ensembles) */
  private roleDiffersFromBaseline(role: PurchaseActorRoleEnum): boolean {
    const current = this.currentRoleIds(role);
    const baseline = this.baselineRoleIds(role);
    return current.length !== baseline.length || current.some((id, i) => id !== baseline[i]);
  }

  private roleIsEmpty(role: PurchaseActorRoleEnum): boolean {
    return this.currentRoleIds(role).length === 0;
  }

  canResetRole(role: PurchaseActorRoleEnum): boolean {
    return this.roleDiffersFromBaseline(role);
  }

  resetRoleLabel(role: PurchaseActorRoleEnum): string {
    return this.roleWasExplicitAtSeed(role) ? "Revenir à l'héritage" : 'Rétablir les valeurs héritées';
  }

  /** Re-seed le champ avec les valeurs HÉRITÉES (l'héritage « nu » du back) : l'utilisateur
   * VOIT ce que l'héritage rapportera ; au save, valeur == héritage => rôle omis => hérite
   * (la surcharge existante disparaît le cas échéant). */
  resetRole(role: PurchaseActorRoleEnum): void {
    const restore = [...(this.roleBaseline.get(role) ?? [])];
    const seedValue = this.isMultiRole(role) ? restore : (restore[0] ?? null);
    if (this.isMultiRole(role)) {
      this.rolePreselected = { ...this.rolePreselected, [role]: [...restore] };
    }
    this.form.get(role)?.setValue(seedValue, { emitEvent: false });
    // Recréation du champ : purge la sélection interne du composant partagé (pas d'API de
    // désélection externe).
    this.roleFieldEpoch = { ...this.roleFieldEpoch, [role]: (this.roleFieldEpoch[role] ?? 0) + 1 };
    this.recomputeDirty();
  }

  /** Le rôle partira-t-il dans le payload au save ? (même règle que buildAssignPayload) */
  private roleWillBeSent(role: PurchaseActorRoleEnum): boolean {
    return !this.roleIsEmpty(role) && this.roleDiffersFromBaseline(role);
  }

  roleBadgeLabel(role: PurchaseActorRoleEnum): string {
    if (this.roleWasExplicitAtSeed(role)) {
      // Surcharge existante : elle survit si le champ dévie encore de l'héritage, sinon
      // (ré-aligné OU vidé) le save l'efface — le rôle redevient hérité.
      return this.roleWillBeSent(role) ? this.sourceLabel(role) : "Reviendra à l'héritage";
    }
    return this.roleWillBeSent(role) ? 'Sera surchargé' : this.sourceLabel(role);
  }

  roleBadgeClass(role: PurchaseActorRoleEnum): string {
    if (this.roleWasExplicitAtSeed(role)) {
      return this.roleWillBeSent(role) ? this.badgeClass(role) : 'pa-badge-parent';
    }
    return this.roleWillBeSent(role) ? 'pa-badge-own' : this.badgeClass(role);
  }

  /** Initialise paresseusement la source d'autocomplete d'un rôle (recherche vide = première page). */
  private ensureRoleItems(role: PurchaseActorRoleEnum): void {
    if (!this.roleItems$[role]) {
      this.searchRoleUsers(role);
    }
  }

  /** fullName absent des snapshots persistés — requis par displayProperty="fullName". */
  private withFullName(contact: PurchaseActorContact | null): PurchaseActorContact | null {
    if (!contact) {
      return contact;
    }
    return { ...contact, fullName: contact.fullName ?? `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() };
  }

  // --- Provenance ---

  sourceLabel(role: PurchaseActorRoleEnum): string {
    const info = this.resolved?.[role];
    return this.formatSourceLabel(info?.source ?? null, info?.node?.label);
  }

  /** Nomenclature ALIGNÉE sur la Supervision métier (default-supervision-tree#provenance) :
   * « Établissement »/« Direct » (configuré ici), « Hérité — {nœud} », « Config client (— {nœud}) »,
   * « Non couvert ». Le niveau « défaut » est présenté comme un héritage de la card « Acteurs par
   * défaut ». */
  private formatSourceLabel(source: PurchaseActorSourceEnum | null, nodeLabel?: string): string {
    const tenantScope = this.scope === PurchaseActorScopeEnum.TENANT;
    switch (source) {
      case PurchaseActorSourceEnum.ESTABLISHMENT_CATEGORY:
        return 'Établissement';
      case PurchaseActorSourceEnum.ESTABLISHMENT_ANCESTOR:
        return `Hérité — ${nodeLabel ?? 'famille parente'}`;
      case PurchaseActorSourceEnum.ESTABLISHMENT_DEFAULT:
        // Le nœud « Acteurs par défaut » LUI-MÊME est édité : la valeur qui s'y résout
        // est sa config PROPRE, pas un héritage.
        return this.isDefaultNode ? 'Établissement' : `Hérité — ${PURCHASE_ACTOR_DEFAULT_NODE_LABEL}`;
      case PurchaseActorSourceEnum.TENANT_CATEGORY:
        return tenantScope ? 'Direct' : 'Config client';
      case PurchaseActorSourceEnum.TENANT_ANCESTOR:
        return tenantScope ? `Hérité — ${nodeLabel ?? 'famille parente'}` : `Config client — ${nodeLabel ?? ''}`;
      case PurchaseActorSourceEnum.TENANT_DEFAULT:
        if (tenantScope) {
          return this.isDefaultNode ? 'Direct' : `Hérité — ${PURCHASE_ACTOR_DEFAULT_NODE_LABEL}`;
        }
        return `Config client — ${PURCHASE_ACTOR_DEFAULT_NODE_LABEL}`;
      default:
        return 'Non couvert';
    }
  }

  badgeClass(role: PurchaseActorRoleEnum): string {
    return this.badgeClassForSource(this.resolved?.[role]?.source ?? null);
  }

  /** Familles de style alignées sur la Supervision métier : own (primaire), parent/hérité
   * (accent), tenant/config client (ardoise pointillée), missing (ambre). En scope TENANT,
   * les niveaux tenant se reclassent own/parent (c'est le scope « local »). */
  private badgeClassForSource(source: PurchaseActorSourceEnum | null): string {
    if (source === null) {
      return 'pa-badge-missing';
    }
    const tenantScope = this.scope === PurchaseActorScopeEnum.TENANT;
    // Config propre du nœud édité : la famille elle-même, OU le nœud « Acteurs par défaut »
    // quand c'est lui qui est édité (rien au-dessus dans son scope).
    if (
      source === PurchaseActorSourceEnum.ESTABLISHMENT_CATEGORY ||
      (tenantScope && source === PurchaseActorSourceEnum.TENANT_CATEGORY) ||
      (this.isDefaultNode &&
        (source === PurchaseActorSourceEnum.ESTABLISHMENT_DEFAULT ||
          (tenantScope && source === PurchaseActorSourceEnum.TENANT_DEFAULT)))
    ) {
      return 'pa-badge-own';
    }
    if (
      !tenantScope &&
      (source === PurchaseActorSourceEnum.TENANT_CATEGORY ||
        source === PurchaseActorSourceEnum.TENANT_ANCESTOR ||
        source === PurchaseActorSourceEnum.TENANT_DEFAULT)
    ) {
      return 'pa-badge-tenant';
    }
    return 'pa-badge-parent';
  }

  // --- Autocomplete factorisé (un seul point pour tous les rôles) ---

  searchRoleUsers(role: PurchaseActorRoleEnum, search = ''): void {
    const filters: { [key: string]: unknown } = { [PURCHASE_ACTOR_ROLE_FILTER_FLAG[role]]: true };
    const establishmentId = this.scope === PurchaseActorScopeEnum.ESTABLISHMENT ? this.establishment?._id : undefined;
    if (establishmentId) {
      filters.establishment = establishmentId;
    }
    if (!this.authService.checkCurrentIsPredik()) {
      filters.isPredik = false;
    }
    this.roleItems$ = {
      ...this.roleItems$,
      [role]: this.userProfileService
        .findAll(null, null, 'fullName', 'asc', search, filters)
        .pipe(map(res => res.data)),
    };
  }

  // --- Autorisation sans commande (invariant PRED-3207 aménagé, PRED-3139) ---
  // Trois états, vivants pendant la saisie :
  //   1. l'approbateur sera STOCKÉ sur ce nœud (champ non-vide ET ≠ héritage) => toggle forcé ON,
  //      non modifiable (approbateur propre ⇔ toujours autorisé — la lettre de PRED-3207) ;
  //   2. aucun approbateur effectif (champ vide ET héritage « nu » vide) => toggle forcé OFF,
  //      non modifiable (jamais autorisé sans approbateur) ;
  //   3. approbateur HÉRITÉ => toggle ÉDITABLE : opt-out (`false`) / réactivation (`true`)
  //      possibles par (sous-)famille — même règle de diff que les rôles au save.

  /** L'approbateur du formulaire sera-t-il stocké sur CE nœud au save ? (même règle que le payload) */
  private get approverWillBeLocal(): boolean {
    return this.roleWillBeSent(PurchaseActorRoleEnum.APPROVER);
  }

  /** Un approbateur sera-t-il effectif après save ? (champ non-vide, sinon héritage « nu ») */
  private get approverEffectivePresent(): boolean {
    const approver = this.form.get(PurchaseActorRoleEnum.APPROVER)?.value as PurchaseActorContact | null;
    if (approver?._id) {
      return true;
    }
    return !!this.inherited?.[PurchaseActorRoleEnum.APPROVER]?.users?.length;
  }

  get authorizationToggleDisabled(): boolean {
    if (!this.canModify) {
      return true;
    }
    return this.approverWillBeLocal || !this.approverEffectivePresent;
  }

  /** Aligne la valeur du toggle sur les états forcés 1 et 2 (l'état 3 laisse la main à l'utilisateur). */
  private enforceAutorizationOrderCoherence(): void {
    const control = this.form?.get('autorizationOrder');
    if (!control) {
      return;
    }
    if (this.approverWillBeLocal && control.value !== true) {
      control.setValue(true, { emitEvent: false });
    } else if (!this.approverEffectivePresent && control.value !== false) {
      control.setValue(false, { emitEvent: false });
    }
    // Verrou porté par le FormControl : un [disabled] sur un champ formControlName est écrasé par
    // setDisabledState(control.disabled) à l'attache du contrôle (Angular ≥ 15) — le toggle restait
    // actif à l'ouverture d'une famille. Les lectures passent par control.value (conservée désactivé).
    if (this.authorizationToggleDisabled && control.enabled) {
      control.disable({ emitEvent: false });
    } else if (!this.authorizationToggleDisabled && control.disabled) {
      control.enable({ emitEvent: false });
    }
  }

  /** Réinitialise le flag vers l'héritage — indépendant du reset de l'APPROVER
   * (règle diff : valeur ≠ héritage => envoyée). */
  revertAutorizationOrder(): void {
    this.form
      .get('autorizationOrder')
      ?.setValue(this.inherited?.autorizationOrder?.value ?? false, { emitEvent: false });
    this.recomputeDirty();
  }

  /** Le flag était-il défini sur le doc explicite au seed ? (miroir de roleWasExplicitAtSeed) */
  get autorizationOrderWasExplicit(): boolean {
    return this.explicit?.autorizationOrder !== undefined;
  }

  /** Valeur du flag ≠ héritage « nu » => partira dans le payload (même règle diff que les rôles). */
  get autorizationOrderDiffersFromInherited(): boolean {
    return !!this.form.get('autorizationOrder')?.value !== (this.inherited?.autorizationOrder?.value ?? false);
  }

  /** Provenance du flag — même formatage que les rôles, mais résolu séparément
   * (`resolved.autorizationOrder`, pas de `node` associé côté back). */
  autorizationOrderSourceLabel(): string {
    // État 1 : le flag suit l'approbateur posé sur le nœud et ne part jamais au save — même badge que lui.
    if (this.approverWillBeLocal) {
      return this.roleBadgeLabel(PurchaseActorRoleEnum.APPROVER);
    }
    if (this.autorizationOrderWasExplicit) {
      return this.autorizationOrderDiffersFromInherited
        ? this.formatSourceLabel(this.resolved?.autorizationOrder?.source ?? null)
        : "Reviendra à l'héritage";
    }
    return this.autorizationOrderDiffersFromInherited
      ? 'Sera surchargé'
      : this.formatSourceLabel(this.resolved?.autorizationOrder?.source ?? null);
  }

  autorizationOrderBadgeClass(): string {
    if (this.approverWillBeLocal) {
      return this.roleBadgeClass(PurchaseActorRoleEnum.APPROVER);
    }
    if (this.autorizationOrderWasExplicit) {
      return this.autorizationOrderDiffersFromInherited
        ? this.badgeClassForSource(this.resolved?.autorizationOrder?.source ?? null)
        : 'pa-badge-parent';
    }
    return this.autorizationOrderDiffersFromInherited
      ? 'pa-badge-own'
      : this.badgeClassForSource(this.resolved?.autorizationOrder?.source ?? null);
  }

  // --- Enregistrement / annulation / retrait ---

  cancelEdits(): void {
    this.clearChildrenOverrides = false;
    this.seedForm();
  }

  get hasExplicitConfig(): boolean {
    return !!this.explicit;
  }

  private extractIds(value: unknown): string[] {
    const contacts = (value as PurchaseActorContact[] | null) ?? [];
    return contacts.filter(contact => !!contact?._id).map(contact => contact._id);
  }

  private applyRoleToPayload(payload: PurchaseActorAssignPayload, role: PurchaseActorRoleEnum, ids: string[]): void {
    switch (role) {
      case PurchaseActorRoleEnum.BUISNESS_REFERENTS:
        payload.buisnessReferents = ids;
        break;
      case PurchaseActorRoleEnum.REFERENTS_N1:
        payload.referentsN1 = ids;
        break;
      case PurchaseActorRoleEnum.REFERENTS_N2:
        payload.referentsN2 = ids;
        break;
      case PurchaseActorRoleEnum.SUPERVISIONS:
        payload.supervisions = ids;
        break;
      default:
        break;
    }
  }

  private buildAssignPayload(): PurchaseActorAssignPayload {
    const payload: PurchaseActorAssignPayload = {
      scope: this.scope,
      clearChildrenOverrides: this.clearChildrenOverrides,
    };
    if (this.scope === PurchaseActorScopeEnum.ESTABLISHMENT && this.establishment) {
      const establishment: PurchaseActorEstablishmentRef = {
        _id: this.establishment._id,
        label: this.establishment.label,
        technicalIdentifier: this.establishment.technicalIdentifier,
      };
      payload.establishment = establishment;
    }
    if (this.category?._id) {
      payload.categoryId = this.category._id;
    }

    // Option A — surcharge = DIFF avec l'héritage « nu » (résolution sans la config propre du
    // nœud) : un rôle part dans le payload ssi son champ est non-vide ET dévie de l'héritage.
    // Rôle omis => $unset back => hérite. Cas couverts par la même règle : surcharge existante
    // ré-alignée sur l'héritage => omise => disparaît (résidu explicite minimal) ; champ vidé
    // => omis => hérite (« explicitement personne » n'existe pas, cf. spec Q2).
    for (const role of PURCHASE_ACTOR_MULTI_ROLES) {
      const ids = this.extractIds(this.form.get(role)?.value);
      if (ids.length && this.roleDiffersFromBaseline(role)) {
        this.applyRoleToPayload(payload, role, ids);
      }
    }

    const approver = this.form.get(PurchaseActorRoleEnum.APPROVER)?.value as PurchaseActorContact | null;
    if (approver?._id && this.roleDiffersFromBaseline(PurchaseActorRoleEnum.APPROVER)) {
      payload.approver = approver._id;
    }

    // Flag « Autorisation sans commande » (invariant PRED-3207 aménagé) : envoyé UNIQUEMENT
    // quand l'approbateur reste hérité (un approbateur stocké sur le nœud force l'autorisation,
    // le back ignorerait/purgerait la valeur) ET que le toggle dévie de l'héritage « nu » —
    // même règle de diff que les rôles. Absent du payload => `$unset` back => hérite du signal.
    if (!this.approverWillBeLocal && this.autorizationOrderDiffersFromInherited) {
      payload.autorizationOrder = !!this.form.get('autorizationOrder')?.value;
    }

    return payload;
  }

  save(): void {
    if (!this.canModify || this.saving) {
      return;
    }
    // Nœud avec sous-familles : demander (modale, calque Supervision métier) si l'enregistrement
    // doit aussi réinitialiser les surcharges des sous-familles ('all') ou n'affecter que
    // celles restées par défaut ('onlyDefault' — les surcharges conservent leurs acteurs).
    if (this.childrenCount > 0) {
      this.dialog
        .open(PurchaseActorApplyModalComponent, {
          width: '560px',
          data: { nodeLabel: this.currentNodeLabel, childrenCount: this.childrenCount, scope: this.scope },
        })
        .afterClosed()
        .subscribe((choice?: PurchaseActorApplyChoice) => {
          if (!choice) {
            return; // Annuler : on reste en édition, rien n'est envoyé.
          }
          this.clearChildrenOverrides = choice === 'all';
          this.performSave();
        });
      return;
    }
    this.clearChildrenOverrides = false;
    this.performSave();
  }

  private performSave(): void {
    this.saving = true;
    this.purchaseActorService.assign(this.buildAssignPayload()).subscribe({
      next: () => {
        this.saving = false;
        this.snackbarService.success('Configuration enregistrée.');
        this.loadResolve();
        this.saved.emit();
      },
      error: err => {
        this.saving = false;
        // Le back remonte des erreurs métier nominatives (contacts invalides/archivés, type
        // de contact manquant) — les afficher telles quelles plutôt qu'un générique muet.
        const serverMessage = typeof err?.error?.message === 'string' ? err.error.message : null;
        this.snackbarService.danger(serverMessage || "Erreur lors de l'enregistrement de la configuration.");
      },
    });
  }

  /** Retrait de la config explicite — garde-fou de saisie non enregistrée (`confirmDiscardIfDirty`). */
  unassignConfig(): void {
    if (!this.canModify) {
      return;
    }
    this.confirmDiscardIfDirty(() => this.performUnassign());
  }

  private performUnassign(): void {
    const payload: PurchaseActorUnassignPayload = { scope: this.scope };
    if (this.scope === PurchaseActorScopeEnum.ESTABLISHMENT && this.establishment) {
      payload.establishmentId = this.establishment._id;
    }
    if (this.category?._id) {
      payload.categoryId = this.category._id;
    }
    this.purchaseActorService.unassign(payload).subscribe({
      next: () => {
        this.snackbarService.success('Configuration retirée.');
        this.loadResolve();
        this.saved.emit();
      },
      error: () => this.snackbarService.danger('Erreur lors du retrait de la configuration.'),
    });
  }
}
